import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import TypeBadge from './TypeBadge';
import Sprite from './Sprite';
import { useDexControls } from './dexControls';
import { getDexEntry, getPokemon, getRandomPokemon, getUsage } from '../services/pokemonService';
import UsageSets from './UsageSets';
import { formatKilograms, formatMeters } from '../utils/dex';
import {
  adjacentId,
  formatDexNumber,
  formatName,
  isPokemon,
  STATS,
  toQuery,
  TYPE_COLORS,
} from '../utils/pokemon';

function PokemonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [entry, setEntry] = useState(null);
  const [flavorText, setFlavorText] = useState('');
  const [flavorVersion, setFlavorVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [partnerError, setPartnerError] = useState('');
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setEntry(null);
    setFlavorText('');
    setFlavorVersion('');

    getDexEntry(id)
      .then((data) => {
        if (!active) return;
        setEntry(data.pokemon);
        setFlavorText(data.flavorText || '');
        setFlavorVersion(data.flavorVersion || '');
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || 'Could not load that entry.');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const loadUsage = useCallback((fresh = false) => {
    setUsageLoading(true);
    setUsageError('');
    getUsage(id, { fresh })
      .then((data) => {
        setUsage(data);
        setUsageLoading(false);
      })
      .catch((err) => {
        setUsage(null);
        setUsageError(err.message || 'Smogon moveset data is unavailable right now.');
        setUsageLoading(false);
      });
  }, [id]);

  useEffect(() => {
    loadUsage(false);
  }, [loadUsage]);

  const previousId = entry ? adjacentId(entry.id, -1) : null;
  const nextId = entry ? adjacentId(entry.id, 1) : null;
  const compareTarget = entry ? (toQuery(entry.name) || String(entry.id)) : '';

  const goTo = (next) => {
    if (!next) return;
    navigate(`/pokemon/${next}`, {
      state: {
        pokemon1: location.state?.pokemon1,
        pokemon2: location.state?.pokemon2,
      },
    });
  };

  const handleBack = () => {
    const fromState = location.state;
    if (isPokemon(fromState?.pokemon1) && isPokemon(fromState?.pokemon2)) {
      navigate('/compare', {
        state: { pokemon1: fromState.pokemon1, pokemon2: fromState.pokemon2 },
      });
      return;
    }
    navigate('/');
  };

  const compareWithRandom = async () => {
    if (!entry) return;
    setPartnerLoading(true);
    setPartnerError('');
    try {
      const current = await getPokemon(compareTarget);
      let partner = await getRandomPokemon();
      if (partner.id === current.id) partner = await getRandomPokemon();
      navigate('/compare', { state: { pokemon1: current, pokemon2: partner } });
    } catch (err) {
      setPartnerError(err.message || 'Could not draw a partner.');
      setPartnerLoading(false);
    }
  };

  useDexControls({
    onLeft: () => goTo(previousId),
    onRight: () => goTo(nextId),
    onA: () => {
      if (compareTarget) navigate(`/compare?with=${encodeURIComponent(compareTarget)}`);
    },
    onB: handleBack,
  });

  const cameFromCompare = isPokemon(location.state?.pokemon1) && isPokemon(location.state?.pokemon2);
  const [typeColor] = TYPE_COLORS[entry?.types?.[0]] || ['#1c3d32'];

  return (
    <div className="page">
      <div className="toolbar">
        <button className="btn btn-ghost" type="button" onClick={handleBack}>
          {cameFromCompare ? 'Back to comparison' : 'Back to Poke-Blunder'}
        </button>
      </div>

      {loading && <p className="status" role="status">Loading entry…</p>}
      {error && <p className="alert" role="alert">{error}</p>}

      {entry && !loading && (
        <>
          <div className="pager">
            <button className="btn btn-ghost" type="button" onClick={() => goTo(previousId)} disabled={!previousId}>
              Previous
            </button>
            <span>{formatDexNumber(entry.id)}</span>
            <button className="btn btn-ghost" type="button" onClick={() => goTo(nextId)} disabled={!nextId}>
              Next
            </button>
          </div>

          <article className="detail-hero">
            <div className="portrait portrait-large" style={{ '--portrait': typeColor }}>
              <Sprite
                src={entry.sprite}
                fallback={entry.spriteFallback}
                alt={`${entry.name} artwork`}
              />
            </div>
            <div>
              <p className="eyebrow">{formatDexNumber(entry.id)}</p>
              <h1>{entry.name}</h1>
              <ul className="type-list">
                {entry.types.map((type) => (
                  <li key={type}>
                    <TypeBadge type={type} />
                  </li>
                ))}
              </ul>
              <blockquote className="flavor">
                <p>{flavorText || 'No flavor text is available for this entry.'}</p>
                {flavorVersion && <footer>PokéAPI · {formatName(flavorVersion)}</footer>}
              </blockquote>
              <dl className="facts">
                <div>
                  <dt>Height</dt>
                  <dd>{formatMeters(entry.heightM)}</dd>
                </div>
                <div>
                  <dt>Weight</dt>
                  <dd>{formatKilograms(entry.weightKg)}</dd>
                </div>
                <div>
                  <dt>Base total</dt>
                  <dd>{entry.total}</dd>
                </div>
              </dl>
              <h2>Abilities</h2>
              <ul className="ability-list">
                {entry.abilities.map((ability) => (
                  <li key={`${ability.name}-${ability.isHidden ? 'h' : 'n'}`}>
                    {ability.name}
                    {ability.isHidden ? ' (hidden)' : ''}
                  </li>
                ))}
              </ul>
              {(entry.baseName || entry.megas?.length > 0) && (
                <div className="forme-links">
                  <h2>
                    {entry.primal || (entry.megas?.length > 0 && entry.megas.every((form) => form.kind === 'primal'))
                      ? 'Primal Reversion'
                      : 'Mega Evolution'}
                  </h2>
                  {entry.baseName && (
                    <Link className="btn btn-secondary" to={`/pokemon/${entry.baseId}`}>
                      {entry.baseName}
                    </Link>
                  )}
                  {(entry.megas || []).map((form) => (
                    <Link key={form.slug} className="btn btn-secondary" to={`/pokemon/${form.slug}`}>
                      {form.name}
                    </Link>
                  ))}
                </div>
              )}
              <div className="toolbar">
                <Link className="btn btn-primary" to={`/compare?with=${encodeURIComponent(compareTarget)}`}>
                  Compare
                </Link>
                {usage?.agents?.available && (
                  <Link className="btn btn-secondary" to={`/pokemon/${entry.slug || entry.id}/agents`}>
                    Agents
                  </Link>
                )}
                <button className="btn btn-secondary" type="button" onClick={compareWithRandom} disabled={partnerLoading}>
                  {partnerLoading ? 'Drawing a partner…' : 'Compare with a random partner'}
                </button>
              </div>
              {partnerError && <p className="alert" role="alert">{partnerError}</p>}
            </div>
          </article>

          <section className="stat-board" aria-label={`${entry.name} base stats`}>
            <h2>Base stats</h2>
            <div className="solo-stats">
              {STATS.map(({ key, label }) => (
                <div className="solo-stat" key={key}>
                  <div className="solo-stat-label">
                    <span>{label}</span>
                    <span>{entry.stats[key]}</span>
                  </div>
                  <div className="bar bar-solo" aria-hidden="true">
                    <span
                      className="fill"
                      style={{ width: `${Math.round((entry.stats[key] / 255) * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <UsageSets
            usage={usage}
            loading={usageLoading}
            error={usageError}
            onRetry={() => loadUsage(true)}
          />
        </>
      )}
    </div>
  );
}

export default PokemonDetail;
